import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { Grid, Icon, List, Popup } from "semantic-ui-react";
import styled, { ThemeContext } from "styled-components";

import type { OpenAPI } from "simplydo/interfaces";
import { Input } from "simplydo/react/component-library";
import { useThrottle } from "simplydo/react/hooks";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import { useContainerDimensions } from "utils/useContainerDimensions";
import util from "utils/utils";

import { StyledSearchItem } from "./SearchItem";
import UserSearchItem from "./UserSearchItem";
import { type SpotlightResult, getPath } from "./locations";

const FadeIn = styled.div`
  animation: fadeIn 0.3s ease-in-out forwards;
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-5px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const StyledSearchBar = styled.div`
  margin-right: ${({ theme }) => (theme.sizes.isMobile ? 0 : 20)}px;
  width: 100%;
  .ui.input {
    border-radius: ${({ theme }) =>
      theme.sizes.isMobile ? "0px" : "4px"}!important;
    background-color: #e9ebee;
    width: 100%;
    input {
      border-top: 0;
      border-left: 0;
      border-right: 0;
      border-radius: ${({ theme }) => (theme.sizes.isMobile ? 0 : 4)}px;
      background-color: #e9ebee;
    }
  }
`;

export const NoSearchContainer = styled(FadeIn)<{ $width: number }>`
  width: 170px;
  width: ${({ theme, $width }) =>
    theme.sizes.isMobile ? "calc(100vw - 30px)" : `calc(${$width}px - 30px)`};
  min-width: ${({ theme }) => (theme.sizes.isMobile ? "0" : "600px")};
  overflow-wrap: break-word;
  overflow: hidden;
  padding: 1rem;
`;

export const StyledSearchResults = styled(FadeIn)<{ $width: number }>`
  max-height: 760px;
  overflow: auto;
  width: ${({ theme, $width }) =>
    theme.sizes.isMobile ? "calc(100vw - 30px)" : `calc(${$width}px - 30px)`};
  min-width: ${({ theme }) => (theme.sizes.isMobile ? "0" : "600px")};
  padding: 1rem;

  h4 {
    margin-bottom: 5px;
  }
  .ui.list {
    margin-top: 0;
  }
`;

export const StyledTitleRow = styled.div<{ $spaceTop?: boolean }>`
  display: flex;
  flex-direction: row;
  margin: 0 !important;
  ${({ $spaceTop }) => $spaceTop && "margin-top: 10px !important;"}
`;

export const StyledTitle = styled.h4`
  margin: 0;
`;

export const StyledSpotlightResult = styled.div<{ $active?: boolean }>`
  background-color: #e9ebee;
  padding: 10px;
  width: 100%;
  display: flex;
  flex-direction: row;
  margin: 5px 10px;
  border-radius: 5px;
  ${({ $active }) => $active && "background-color: #ced3d9;"}
`;

type SearchResults = OpenAPI.GET<"/search/omni">["response"];
type RecentSearches =
  OpenAPI.GET<"/search/omni/recent">["response"]["recentSearches"];
const searchResultsAreEmpty = (searchResults: Partial<SearchResults>) => {
  return (
    util.isEmpty(searchResults) ||
    Object.values(searchResults).every((val) => val.length === 0)
  );
};

type OmniBarProps = { onClose?: () => void };

const OmniBar = ({ onClose }: OmniBarProps) => {
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const onSwitchOrganisation = useCallback(
    (org) => {
      dispatch(actions.users.switchOrganisation(org));
    },
    [dispatch],
  );

  const theme = useContext(ThemeContext);
  const searchInput = useRef<HTMLInputElement>();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<Partial<SearchResults>>(
    {},
  );
  const [searching, setSearching] = useState(false);
  const [hovered, setHovered] = useState<any>({});
  const [spotlightResults, setSpotlightResults] = useState<SpotlightResult[]>(
    [],
  );
  const [recentSearches, setRecentSearches] = useState<RecentSearches>([]);
  const [cursor, setCursor] = useState(0);
  const { width } = useContainerDimensions(searchInput);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    users = [],
    challenges = [],
    ideas = [],
    groups = [],
    organisations = [],
    tags = [],
  } = searchResults;
  const totalResults = useMemo(
    () =>
      searchTerm ?
        [
          ...spotlightResults,
          ...users,
          ...challenges,
          ...ideas,
          ...groups,
          ...(organisations || []),
          ...tags,
        ]
      : recentSearches,
    [
      searchTerm,
      spotlightResults,
      users,
      challenges,
      ideas,
      groups,
      organisations,
      tags,
      recentSearches,
    ],
  );

  const userStartIndex = spotlightResults.length;
  const challengeStartIndex = userStartIndex + searchResults.users?.length || 0;
  const ideaStartIndex =
    challengeStartIndex + searchResults.challenges?.length || 0;
  const groupStartIndex = ideaStartIndex + searchResults.ideas?.length || 0;
  const organisationStartIndex =
    groupStartIndex + searchResults.groups?.length || 0;
  const tagStartIndex =
    organisationStartIndex + searchResults.organisations?.length || 0;

  useEffect(() => {
    api.search.getRecentSearches(
      ({ recentSearches: newRecentSearches }) =>
        setRecentSearches(newRecentSearches),
      () => {},
    );
  }, []);

  const addRecentSearch = useCallback((type, name) => {
    api.search.recentSearch(
      { type, search: name },
      (data) => {
        setRecentSearches(data.recentSearches);
      },
      () => {},
    );
  }, []);

  const switchOrganisation = useCallback(
    (_e, org) => {
      api.users.switchOrganisation(
        user._id,
        org._id,
        (data) => {
          onSwitchOrganisation(data.organisation);
        },
        (err) => toast.error(err.message),
      );
    },
    [user, onSwitchOrganisation],
  );

  const finishSearch = useCallback(() => {
    setOpen(false);
    setSearchTerm("");
    setSearchResults({});
    setSpotlightResults([]);
    setCursor(0);
    setSearching(false);
    setHovered({});
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function downHandler({ key }) {
      switch (key) {
        case "ArrowUp": {
          const nextCursor =
            cursor === 0 ? totalResults.length - 1 : cursor - 1;
          setCursor(nextCursor);
          setHovered(totalResults[nextCursor]);
          break;
        }
        case "ArrowDown": {
          const nextCursor =
            cursor === totalResults.length - 1 ? 0 : cursor + 1;
          setCursor(nextCursor);
          setHovered(totalResults[nextCursor]);
          break;
        }
        case "Enter": {
          const isSpotlight = cursor < userStartIndex;
          const isUser =
            cursor >= userStartIndex && cursor < challengeStartIndex;
          const isChallenge =
            cursor >= challengeStartIndex && cursor < ideaStartIndex;
          const isIdea = cursor >= ideaStartIndex && cursor < groupStartIndex;
          const isGroup =
            cursor >= groupStartIndex && cursor < organisationStartIndex;
          const isOrganisation = cursor >= organisationStartIndex;
          const isTag = cursor >= tagStartIndex;
          if (spotlightResults.length) {
            if (isSpotlight) {
              navigate(hovered.link);
            } else if (isUser) {
              addRecentSearch("user", hovered.profile.fullName);
              navigate(`/users/${hovered._id}`);
            } else if (isChallenge) {
              addRecentSearch("challenge", hovered.name);
              navigate(`/challenges/${hovered._id}`);
            } else if (isIdea) {
              addRecentSearch("idea", hovered.name);
              navigate(`/ideas/${hovered._id}`);
            } else if (isGroup) {
              addRecentSearch("group", hovered.name);
              navigate(`/groups/${hovered._id}`);
            } else if (isOrganisation) {
              addRecentSearch("organisation", hovered.name);
              switchOrganisation(null, hovered);
            } else if (isTag) {
              navigate(`/tags/${hovered._id}`);
            }
          }
          break;
        }
      }
    }

    window.addEventListener("keydown", downHandler);
    return () => {
      window.removeEventListener("keydown", downHandler);
    };
  }, [
    challengeStartIndex,
    cursor,
    groupStartIndex,
    hovered,
    ideaStartIndex,
    navigate,
    open,
    organisationStartIndex,
    spotlightResults.length,
    switchOrganisation,
    tagStartIndex,
    totalResults,
    userStartIndex,
    addRecentSearch,
  ]);

  const search = useThrottle(
    (term) => {
      if (!term) return;
      setSearching(true);
      api.search.omni(
        { query: term },
        (results) => {
          setOpen(true);
          setSearching(false);
          setSearchResults(results);
        },
        () => setSearching(false),
      );
    },
    150,
    [],
  );

  const updateSearchTerm = useCallback(
    (e) => {
      const term = e.target.value;
      setSearchTerm(e.target.value);
      if (term) {
        if (term.length >= 2) {
          setSearching(true);
          search(e.target.value);
        }
        setSpotlightResults(getPath(user, term));
      } else {
        setSpotlightResults([]);
        setSearchResults({});
        setSearching(false);
      }
    },
    [search, user],
  );

  const userIsAdmin = util.hasPermission(
    user,
    "org.viewDashboard",
    user?.ownerOrganisation?._id,
  );
  const hasSearchResults = useMemo(
    () => !searchResultsAreEmpty(searchResults),
    [searchResults],
  );

  const hoverControls = (index) => ({
    onMouseEnter: () => {
      if (index >= totalResults.length) {
        setCursor(0);
        setHovered({});
        return;
      }
      setHovered(totalResults[index]);
      setCursor(index);
    },
  });

  return (
    <StyledSearchBar>
      <Popup
        basic
        on="click"
        position="bottom right"
        open={open}
        offset={[0, theme.sizes.isMobile ? -10 : -2]}
        closeOnPortalMouseLeave={false}
        closeOnTriggerMouseLeave={false}
        hideOnScroll={false}
        onClose={() => {
          setOpen(false);
          onClose?.();
        }}
        onOpen={() => {
          api.journey.record("users", null, "clickedOmniBar");
          setOpen(true);
          if (searchInput.current) {
            searchInput.current?.focus?.();
          }
        }}
        style={{
          maxHeight: 800,
          width: theme.sizes.isMobile ? "100vw" : "fit-content",
        }}
        trigger={
          <div>
            <Input
              autoFocus={theme.sizes.isMobile}
              value={searchTerm}
              icon="search"
              iconPosition="left"
              style={{ border: "0px" }}
              size="small"
              placeholder={
                !userIsAdmin ?
                  `Search ${util.appName()}...`
                : t("omni.search.placeholder")
              }
              onChange={updateSearchTerm}
              loading={searching}
              ref={searchInput}
            />
          </div>
        }
        content={
          searchTerm ?
            !hasSearchResults && !spotlightResults.length && !searching ?
              <NoSearchContainer $width={width}>
                <h4 style={{ marginBottom: 10 }}>No results found</h4>
                <p>Try adjusting your search query or check your spelling.</p>
              </NoSearchContainer>
            : <StyledSearchResults $width={width}>
                <Grid stackable>
                  {spotlightResults.length ?
                    <StyledTitleRow $spaceTop>
                      <StyledTitle>Pages</StyledTitle>
                    </StyledTitleRow>
                  : null}
                  {spotlightResults.map((result, i) => (
                    <StyledSpotlightResult
                      key={result.title}
                      $active={cursor === i}
                      as={Link}
                      to={result.link}
                      onClick={finishSearch}
                      {...hoverControls(i)}
                    >
                      <Icon name={result.icon} style={{ marginRight: 5 }} />
                      <p>
                        <b>{result.title}</b> - {result.link}
                      </p>
                    </StyledSpotlightResult>
                  ))}
                  {searchResults.users && searchResults.users.length > 0 ?
                    <Grid.Column width={8} style={{ marginTop: 0 }}>
                      <h4>People</h4>
                      {searchResults.users.map((searchUser, index) => (
                        <UserSearchItem
                          active={index + userStartIndex === cursor}
                          key={searchUser._id}
                          user={searchUser}
                          onClick={() => {
                            addRecentSearch(
                              "user",
                              searchUser.profile.fullName,
                            );
                            finishSearch();
                          }}
                          {...hoverControls(index + userStartIndex)}
                        />
                      ))}
                    </Grid.Column>
                  : null}

                  {(
                    searchResults.challenges &&
                    searchResults.challenges.length > 0
                  ) ?
                    <Grid.Column width={8} style={{ marginTop: 0 }}>
                      <h4>
                        {t("common:capitalise", { key: "generic.challenges" })}
                      </h4>
                      <List>
                        {searchResults.challenges.map((challenge, index) => (
                          <StyledSearchItem
                            $active={index + challengeStartIndex === cursor}
                            as={Link}
                            to={`/challenges/${challenge._id}`}
                            key={challenge._id}
                            onClick={() => {
                              addRecentSearch("challenge", challenge.name);
                              finishSearch();
                            }}
                            {...hoverControls(index + challengeStartIndex)}
                          >
                            <Icon name="target" />
                            <span>{challenge.name}</span>
                          </StyledSearchItem>
                        ))}
                      </List>
                    </Grid.Column>
                  : null}

                  {searchResults.ideas && searchResults.ideas.length > 0 ?
                    <Grid.Column width={8}>
                      <h4>
                        {t("common:capitalise", { key: "generic.ideas" })}
                      </h4>
                      <List>
                        {searchResults.ideas.map((idea, index) => (
                          <StyledSearchItem
                            $active={index + ideaStartIndex === cursor}
                            as={Link}
                            to={`/ideas/${idea._id}`}
                            key={idea._id}
                            onClick={() => {
                              addRecentSearch("idea", idea.name);
                              finishSearch();
                            }}
                            {...hoverControls(index + ideaStartIndex)}
                          >
                            <Icon name="lightbulb" />
                            <span>{idea.name}</span>
                          </StyledSearchItem>
                        ))}
                      </List>
                    </Grid.Column>
                  : null}

                  {searchResults.groups && searchResults.groups.length > 0 ?
                    <Grid.Column width={8}>
                      <h4>Groups</h4>
                      <List>
                        {searchResults.groups.map((group, index) => (
                          <StyledSearchItem
                            $active={index + groupStartIndex === cursor}
                            as={Link}
                            to={`/groups/${group._id}`}
                            key={group._id}
                            onClick={() => {
                              addRecentSearch("group", group.name);
                              finishSearch();
                            }}
                            {...hoverControls(index + groupStartIndex)}
                          >
                            <Icon name="users" />
                            <span>{group.name}</span>
                          </StyledSearchItem>
                        ))}
                      </List>
                    </Grid.Column>
                  : null}

                  {(
                    searchResults.organisations &&
                    searchResults.organisations.length > 0
                  ) ?
                    <Grid.Column width={8}>
                      <h4>Organisations</h4>
                      <List>
                        {searchResults.organisations.map((org, index) => (
                          <StyledSearchItem
                            $active={index + organisationStartIndex === cursor}
                            as={Link}
                            to={"#"}
                            key={org._id}
                            onClick={(e) => {
                              addRecentSearch("organisation", org.name);
                              switchOrganisation(e, org);
                              finishSearch();
                            }}
                            {...hoverControls(index + organisationStartIndex)}
                          >
                            <Icon name="sitemap" />
                            <span>{org.name}</span>
                          </StyledSearchItem>
                        ))}
                      </List>
                    </Grid.Column>
                  : null}
                  {searchResults.tags && searchResults.tags.length > 0 ?
                    <Grid.Column width={8}>
                      <h4>Tags</h4>
                      <List>
                        {searchResults.tags.map((tag, index) => (
                          <StyledSearchItem
                            $active={index + tagStartIndex === cursor}
                            as={Link}
                            to={`/tags/${tag._id}`}
                            key={tag._id}
                            onClick={() => {
                              addRecentSearch("tag", tag.value);
                              finishSearch();
                            }}
                            {...hoverControls(index + tagStartIndex)}
                          >
                            <Icon name="hashtag" />
                            <span>{tag.value}</span>
                          </StyledSearchItem>
                        ))}
                      </List>
                    </Grid.Column>
                  : null}
                </Grid>
              </StyledSearchResults>

          : <NoSearchContainer $width={width}>
              {recentSearches && recentSearches.length > 0 ?
                <>
                  <h4 style={{ marginBottom: 10 }}>Recent searches</h4>
                  {recentSearches.slice(0, 5).map((sr, index) => (
                    <StyledSearchItem
                      key={index}
                      as={Link}
                      onClick={() => {
                        setSpotlightResults([]);
                        setSearchResults({});
                        updateSearchTerm({ target: { value: sr.search } });
                      }}
                      $active={cursor === index}
                      {...hoverControls(index)}
                    >
                      <Icon name="search" />
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span>{sr.search}</span>
                        <span
                          style={{
                            color: "rgba(0,0,0,0.3)",
                            textTransform: "capitalize",
                          }}
                        >
                          {sr.type}
                        </span>
                      </div>
                    </StyledSearchItem>
                  ))}
                </>
              : <>
                  <h4 style={{ marginBottom: 10 }}>{t("omni.search.title")}</h4>
                  <p>{t("omni.search.info")} </p>
                </>
              }
            </NoSearchContainer>
        }
      />
    </StyledSearchBar>
  );
};

export default OmniBar;
